import { values, cloneDeep } from '@dt/fe-utils/dist/lodash'
import React, { useState, useEffect, useRef } from 'react'
import { IonFooter, IonContent, IonToast, IonLoading, IonModal, IonHeader } from '@ionic/react'
import { observer } from 'mobx-react'
import payMobx, { ICard, TPayScene } from '@mobx/pay/pay.mobx'
import { virtualOrderMobx, memberMobx } from 'mobx/mkt'
import { advertMobx, bankCardMobx, payChannelMobx, storeMobx, surchargeMobx } from '@mobxStore'
import { TipModal, TextTipAlert, MktButton, ModalHeader, GlobalPaymentsModal } from '@cps'
import { toDecimal, uuid } from '@dt/fe-utils'
import { modalLayer, getPlatform, miniAppRouteTo } from '@utils'
import ExternalPay from './ExternalPay'
import BankCardAuthModal from './BankCardOnline/BankCardAuthModal'
import env from '@env'
import i18n from 'i18next'
import { TGroupByProductType } from '@common/enum/mkt.enum'
import './index.scss'

interface IProps {
  resolve?: (res: any) => void;
  reject?: () => void;
  history: any;
  productType?: TGroupByProductType;
  scene: TPayScene;
  orderPara: any;
  wxGive?: boolean;
}
interface IBill {
  id: string;
  method?: string;
  card?: ICard;
  amount: number;
  message?: string;
}

const PayModal: React.FC<IProps> = (props: IProps) => {
  const { resolve, reject, scene, orderPara, history, productType, wxGive } = props
  const [tip, setTip] = useState(0)
  const [bills, setBills] = useState<Record<string, IBill>>({})
  const [errorMessage, setErrorMessage] = useState('')
  const [showLoading, setShowLoading] = useState(false)
  const [showPolicy, setShowPolicy] = useState(false)
  const [memberCard, setMemberCard] = useState<ICard>()
  const payingRef = useRef(false) // 防止重复支付（目前只针对购买虚拟商品的时候）

  useEffect(() => {
    const { brandId } = storeMobx.brand
    payMobx.setOrder({
      scene: scene,
      orderId: '',
      brandId,
      storeId: orderPara.storeId,
      subtotal: orderPara.totalFee,
      serviceCharge: 0,
      tax: 0
    })
  }, [])

  useEffect(() => {
    if (memberMobx.isLogin) {
      const card = memberMobx.cardInfoForPay
      if (card && card.card && card.balance > 0) {
        setMemberCard({
          type: 'memberCard',
          id: String(card.memberCardId),
          name: card.card?.cardName,
          number: card.cardNo,
          balance: card.balance,
          isPayTip: true,
        })
      }
    }
  }, [memberMobx.isLogin, memberMobx.cardInfoForPay])

  // 自动生成初始分笔支付
  useEffect(() => {
    let defaultMethod: string = Object.keys(payMobx.availableMethods)[0]
    if (bo === 'cn' && !!payMobx.methodChannelMap.wechatPay) {
      defaultMethod = 'wechatPay'
    }
    const total = orderPara.paidFee
    const methodData = defaultMethod ? payMobx.availableMethods[defaultMethod] : payMobx.availableMethods
    const { method, card, channel } = methodData
    const bills = {}
    if (method) {
      bills[method] = { id: uuid(), method, channel, amount: total, card }
    }
    setBills(bills)
  }, [orderPara, payMobx.methodChannelMap.wechatPay, memberCard, tip, payMobx.firstAvaliableMethod, payMobx.availableMethods])

  const getOpenIdData = async () => {
    const obj = JSON.parse(memberMobx.thirdAuthInfo) || {}
    const { isWeixinMiniProgram } = getPlatform()
    let openIdData: { openId: string; appId?: string }
    if (isWeixinMiniProgram) {
      openIdData = {
        openId: window.Config.openId,
      }
    } else {
      openIdData = {
        openId: obj.gOpenId,
      }
    }
    if (!openIdData.openId) {
      openIdData = await payMobx.fetchGOpenIdAndSave()
    }
    if (isWeixinMiniProgram && !openIdData.appId) {
      openIdData.appId = window.Config.appId
    }
    return openIdData
  }

  const billToChannel = async (bill: IBill, orderId: string): Promise<{
    uniqueId: string;
    amount: number;
    channelType: string;
    ext?: any;
    surchargeInfo?: any;
    onSuccess?(): void;
    onFailed?(): void;
  }> => {
    let channelType = payMobx.methodChannelMap[bill.method]
    let openIdData
    if (['wechatPay', 'alipay'].includes(bill.method)) {
      openIdData = (await getOpenIdData()) || {}
    }
    let ext
    let isSurcharge = false
    if (channelType === 'CardConnectBankCard') {
      if (bill.card) {
        bankCardMobx.setLastUsed({ tokenType: 'multi', creditCardNo: bill.card.id })
        ext = { creditCardNo: bill.card.id }
        isSurcharge = bill.card?.cardType !== 'N'
      } else {
        setShowLoading(false)
        const res: any = await modalLayer(BankCardAuthModal, { payRechargeFlag: true })
        ext = { creditCardNo: res.creditCardNo }
        isSurcharge = res.creditCardType !== 'N'
        setShowLoading(true)
      }
    } else if (channelType === 'HeartLandBankCard') {
      if (bill.card) {
        bankCardMobx.setLastUsed({ tokenType: 'multi', creditCardNo: bill.card.id })
        ext = { creditCardNo: bill.card.id }
        isSurcharge = bill.card?.cardType !== 'N'
      } else {
        const res: any = await modalLayer(BankCardAuthModal, { payRechargeFlag: true, multiUse: false })
        ext = { authCode: res.token }
        isSurcharge = res.creditCardType !== 'N'
        setShowLoading(true)
      }
    } else if (channelType === 'GiftCard') {
      ext = { cardId: bill.card.id, cardNumber: bill.card.number, token: memberMobx.token }
    } else if (channelType === 'MemberBalance') {
      ext = { cardId: bill.card.id, cardNumber: bill.card.number, memeberId: memberMobx.memberInfo?.memberId, token: memberMobx.token }
    } else if (channelType === 'CitconAlipayWeb') {
      ext = { returnUrl: getReturnUrl(orderId) }
    } else if (channelType === 'CitconWechatPayWeb') {
      ext = { returnUrl: getReturnUrl(orderId) }
    } else if (channelType === 'CardConnectApplePay' || channelType === 'GlobalPaymentsApplePay') {
      const total: ApplePayJS.ApplePayLineItem = {
        type: 'final',
        label: 'Dining',
        amount: String(bill.amount)
      }
      return new Promise((resolve, reject) => {
        const session = new ApplePaySession(1, {
          countryCode: 'US',
          currencyCode: 'USD',
          supportedNetworks: ['amex', 'visa', 'masterCard', 'discover'],
          merchantCapabilities: ['supports3DS'],
          total,
        })
        session.onvalidatemerchant = async function (event) {
          try {
            const merchantSession = await payMobx.applePayMerchantValidate(event)
            session.completeMerchantValidation(merchantSession)
          } catch (e) {
            session.abort()
            reject(e)
          }
        }
        session.onpaymentmethodselected = async function (event) {
          session.completePaymentMethodSelection({ newTotal: total })
        }
        session.onpaymentauthorized = async function (event) {
          const { paymentData } = event.payment.token
          const { data, header, signature, version } = paymentData
          const { ephemeralPublicKey, publicKeyHash, transactionId } = header
          resolve({
            uniqueId: bill.id,
            amount: bill.amount,
            channelType,
            ext: {
              data,
              signature,
              version,
              ephemeralPublicKey,
              publicKeyHash,
              transactionId,
            },
            onSuccess () {
              session.completePayment((window as any).ApplePaySession.STATUS_SUCCESS)
            },
            onFailed () {
              session.abort()
            }
          })
        }
        session.oncancel = function () {
          reject('cancel')
        }
        session.begin()
      })
    } else if (channelType === 'StripeApplePay') {
      return new Promise((resolve, reject) => {
        const Stripe = (window as any).Stripe
        Stripe.setPublishableKey(payChannelMobx.channelAccount.StripeApplePay?.publicApiKey)
        const paymentRequest = {
          countryCode: 'US',
          currencyCode: 'USD',
          total: {
            label: storeMobx.info.storeName,
            amount: String(bill.amount),
          }
        }
        const session = Stripe.applePay.buildSession(paymentRequest, (result, completion) => {
          resolve({
            uniqueId: bill.id,
            amount: bill.amount,
            channelType,
            ext: { authCode: result.token.id },
            onSuccess () {
              completion((window as any).ApplePaySession.STATUS_SUCCESS)
            },
            onFailed () {
              completion((window as any).ApplePaySession.STATUS_FAILURE)
            }
          })
        }, reject)
        session.oncancel = function () {
          reject('cancel')
        }
        session.begin()
      })
    } else if (['WechatPayWeb', 'ShengPayWechatWeb', 'ShengPayWechatWebV2'].includes(channelType)) {
      ext = openIdData
    } else if (channelType === 'AlipayWeb') {
      ext = { returnUrl: getReturnUrl(orderId) }
    } else if (['AlipayApp', 'ShengPayAlipayWeb', 'ShengPayAlipayWebV2'].includes(channelType)) {
      ext = {
        buyerId: openIdData.openId
      }
    } else if (channelType === 'PayPalWeb') {
      ext = { returnUrl: getReturnUrl(orderId) }
    }

    // 附加费
    const surchargeConfig = surchargeMobx.surchargeMap?.[channelType]
    const surchargeInfo: any = {}
    if (isSurcharge && surchargeConfig) {
      const surcharge = toDecimal(bill.amount * (surchargeConfig.rate / 100), 2)
      if (surcharge) {
        surchargeInfo.surcharge = surcharge
        surchargeInfo.paymentSurchargeConfigUuid = surchargeConfig.id
        surchargeInfo.surchargeName = surchargeConfig.name
        surchargeInfo.surchargeRate = surchargeConfig.rate
      }
    }

    if (!bill.amount) { // 优惠后付费金额为0时都走零元付
      channelType = 'ZeroPay'
      ext = {}
    }

    return {
      uniqueId: bill.id,
      amount: toDecimal(bill.amount, 2) as any,
      channelType,
      ext,
      surchargeInfo,
    }
  }

  const getReturnUrl = (orderId: string) => {
    const url = window.location.href
    let prefix = url.substr(0, url.indexOf('/pay'))
    if (prefix.indexOf('localhost') > -1) {
      prefix = env.WEBSITE
    }
    const returnUrl = prefix + `/result/${orderId}`
    return returnUrl
  }

  const goResult = (orderId: string) => {
    history.replace(`/result/${orderId}`)
  }

  // 生成订单
  const createOrder = async () => {
    let res: any
    if (['redeemCouponByMoney', 'memberCardRenew', 'memberCardBuy'].includes(orderPara.eatType)) {
      if (payingRef.current && !orderPara.paidFee) { // zeroPay虚拟商品时防止重复下单
        return
      }
      payingRef.current = true
      res = await virtualOrderMobx.placeVirtualOrder(orderPara)
    } else {
      res = await virtualOrderMobx.placeOrder(orderPara)
    }
    try {
      advertMobx.traceBuyAdvert({
        ...res,
        // 加上卡号，订单不返回cardId？？
        ...(['memberCardRenew', 'memberCardBuy', 'memberCz'].includes(orderPara.eatType) ? { cardId: orderPara.details?.[0].productNo } : {})
      })
    } catch (error) {
    }
    if (res.error) {
      if (orderPara.eatType === 'redeemCouponByMoney') {
        payingRef.current = false
        let text: string
        if (res.code === 5016) {
          // 商品下架 找不到
          text = i18n.t('mkt.redeem.buyFailTip2')
        } else if (res.code === 5020) {
          // 不在售卖时间
          text = i18n.t('mkt.redeem.buyFailTip3')
        } else if (res.code === 5021) {
          // 时间不匹配
          text = i18n.t('mkt.redeem.buyFailTip3')
        } else if (res.code === 5018) {
          // 商品售罄
          if (!productType || productType === TGroupByProductType.coupons) {
            text = i18n.t('mkt.redeem.buyFailTip5')
          } else {
            text = i18n.t('mkt.redeem.buyFailTip7')
          }
        } else if (res.code === 5029) {
          // 购买次数已达上限
          text = i18n.t('mkt.redeem.buyFailTip6')
        } else {
          // 其他原因购买失败
          text = i18n.t('mkt.redeem.buyFailTip')
        }
        modalLayer(TipModal, {
          title: i18n.t('mkt.redeem.buyFail'),
          content: text
        })
        resolve(res)
      } else if (['memberCardRenew', 'memberCardBuy'].includes(orderPara.eatType)) {
        payingRef.current = false
        let msg: string = res.message || ''
        if (res.code === 1000) {
          msg = i18n.t('mkt.member.togetherBuying')
        }
        modalLayer(TextTipAlert, {
          title: msg
        }).catch(() => { })
        resolve(res)
      } else {
        let msg: string = res.message || ''
        if (res.code === 5038) {
          msg = i18n.t('mkt.member.notRechargeTip')
        }
        modalLayer(TextTipAlert, {
          title: msg
        }).catch(() => { })
        resolve(res)
      }
    } else {
      if (res && res.ordersId) {
        const prepayCreate = await virtualOrderMobx.startPay({
          orderId: res.ordersId,
          storeId: orderPara.storeId
        })
        if (prepayCreate && prepayCreate.prepayCreateId) {
          await payMobx.init({
            scene,
            orderId: res.ordersId,
            pcid: prepayCreate.prepayCreateId
          })
          pay(res.ordersId)
        }
      }
    }
  }

  const pay = async (orderId: string) => {
    try {
      setShowLoading(true)
      const total = toDecimal(orderPara.paidFee, 2) as any
      const billsArr = values(bills)
      if (billsArr.length > 1) {
        const channels: any[] = await Promise.all(billsArr.map(x => billToChannel(x, orderId)))
        let remainTip = tip
        channels.forEach(x => {
          const channelTip = Math.min(x.amount, remainTip)
          x.tip = toDecimal(channelTip, 2) as any
          remainTip -= channelTip
          x = { ...x, ...x.surchargeInfo }
          delete x.surchargeInfo
        })
        await payMobx.batchPay(orderId, channels)
        goResult(orderId)
      } else {
        const { channelType, ext, surchargeInfo, onSuccess, onFailed } = await billToChannel(billsArr[0], orderId)
        try {
          const result = await payMobx.pay({
            ordersId: orderId,
            amount: total,
            tip,
            channel: channelType,
            channelExt: ext,
            surchargeInfo
          })
          handleSinglePayResult(channelType, result, orderId)
          if (onSuccess) {
            onSuccess()
          }
        } catch (e) {
          payingRef.current = false
          if (e.code === -232) {
            goResult(orderId)
          } else {
            if (onFailed) {
              onFailed()
            }
            throw e
          }
        }
      }
    } catch (e) {
      payingRef.current = false
      if (e.message !== 'CANCEL') {
        setErrorMessage(e.message)
      }
    } finally {
      setShowLoading(false)
    }
  }

  const handleSinglePayResult = (channel: string, result: any, ordersId: string) => {
    if (channel === 'GlobalPaymentsBankCard') {
      const { serverUrl, hppJson } = result
      modalLayer(GlobalPaymentsModal, { serverUrl, hppJson }, 'GlobalPaymentsModal')
        .then(() => {
          goResult(ordersId)
        })
        .catch((error) => {
          setErrorMessage(error || '用户取消')
        })
    } else if (channel === 'CitconAlipayWeb') {
      const { url } = result
      window.location.href = url
    } else if (channel === 'CitconWechatPayWeb') {
      const { url } = result
      window.location.href = url
    } else if (['WechatPayWeb', 'ShengPayWechatWeb', 'ShengPayWechatWebV2'].includes(channel)) {
      const { wapPayRequest } = result
      const { isWeixinMiniProgram, isWeixinBrower } = getPlatform()
      if (['ShengPayWechatWeb', 'ShengPayWechatWebV2'].includes(channel)) {
        wapPayRequest.package = wapPayRequest.packageStr || wapPayRequest.package
      }
      if (isWeixinMiniProgram) {
        miniAppRouteTo({
          url: `/pages/pay/index?p=${encodeURIComponent(JSON.stringify(wapPayRequest))}&o=${ordersId}&scene=${scene}${wxGive ? '&give=1' : ''}`
        })
      } else if (isWeixinBrower) {
        window.WeixinJSBridge.invoke('getBrandWCPayRequest', wapPayRequest, function (res) {
          if (res.err_msg !== 'get_brand_wcpay_request:ok') {
            setErrorMessage('用户取消')
          } else {
            goResult(ordersId)
          }
        })
      } else {
        throw new Error('Not in weixin mini program or browser.')
      }
    } else if (channel === 'AlipayWeb') {
      const { url } = result
      window.location.href = url
    } else if (['AlipayApp', 'ShengPayAlipayWeb', 'ShengPayAlipayWebV2'].includes(channel)) {
      const { wapPayRequest } = result
      let tradeNo = wapPayRequest.tradeNo || wapPayRequest.tradeNO
      // if (['ShengPayAlipayWeb', 'ShengPayAlipayWebV2'].includes(channel)) {
      //   tradeNo = wapPayRequest.tradeNO
      // }
      window.AlipayJSBridge.call('tradePay', { tradeNO: tradeNo }, function (result) {
        if (result.resultCode === '9000') {
          goResult(ordersId)
        } else {
          setErrorMessage('用户取消')
        }
      })
    } else if (channel === 'PayPalWeb') {
      if (result?.token) {
        const { env, envBo } = ENV
        const url = `https://www.${!(env === 'pro' && envBo === 'us') ? 'sandbox.' : ''}paypal.com/checkoutnow?token=${result.token}`
        window.location.href = url
      }
    } else {
      goResult(ordersId)
    }
  }

  let canPay = Object.keys(bills).length > 0
  return (
    <IonModal isOpen onDidDismiss={reject} cssClass='pay-modal'>
      <ModalHeader onClose={reject} title={i18n.t('pay.paymentMethod')} />
      <IonLoading isOpen={showLoading} />
      <IonToast
        position='top'
        color='danger'
        isOpen={!!errorMessage}
        onDidDismiss={() => setErrorMessage('')}
        message={errorMessage}
        duration={2000}
      />
      <IonContent>
        <div className='pay-method-list'>
          <ExternalPay
            bills={bills}
            onSelect={(bill: any, newCard: any) => {
              const { id, method, card } = bill
              let resultBills = cloneDeep(bills)
              if (id) {
                if (newCard) {
                  resultBills[method].card = newCard
                }
              } else {
                resultBills = {
                  [method]: {
                    id: uuid(),
                    method,
                    card,
                    amount: orderPara.paidFee
                  }
                }
              }
              setBills(resultBills)
            }}
          />
        </div>
      </IonContent>
      <IonFooter className='modal-footer'>
        <MktButton disabled={!canPay} className='pay-button' data-cypid='payModalCreateOrder' onClick={createOrder}>{i18n.t('pay.pay')}
        </MktButton>
        {payMobx.policy && (
          <div className='agreement'>
            {i18n.t('pay.agreement')}
            <a data-cypid='payModalShowPolicy' onClick={() => setShowPolicy(true)}>{i18n.t('pay.policy')}</a>
          </div>
        )}
        <div className='copyright'>
          <i className='iconfont icon-PowerbyeShine2x' />
        </div>
      </IonFooter>
      <IonModal isOpen={showPolicy} cssClass='pay-policy-modal'>
        <IonHeader>{i18n.t('pay.policy')}</IonHeader>
        <IonContent>
          <div className='mobile-policy-content' dangerouslySetInnerHTML={{ __html: payMobx.policy }} />
        </IonContent>
        <IonFooter className='submit'>
          <button className='pay-button' data-cypid='payModalHidePolicy' onClick={() => setShowPolicy(false)}>{i18n.t('common.ok')}</button>
        </IonFooter>
      </IonModal>
    </IonModal>
  )
}
export default observer(PayModal)
